import { FunctionField, FunctionFieldProps } from 'react-admin';
import Chip from '@mui/material/Chip';
import { Color } from '@mui/material';
import { rbac } from '@utils';
import { CreditStatus } from '@types';

interface ColoredOption {
  id: CreditStatus;
  name: string;
  color: Color | string;
  disabled?: boolean;
}

export const creditStatusOptions: ColoredOption[] = [
  { id: CreditStatus.DRAFT, name: 'Draft', color: '#74706B' },
  { id: CreditStatus.MODERATION, name: 'Applying', color: '#0A0A5F' },
  { id: CreditStatus.PRE_SCREENING, name: 'Pre Screening', color: '#1D1DB2' },
  { id: CreditStatus.DOC_COLLECTION, name: 'Doc Collection', color: '#3B3BB8' },
  { id: CreditStatus.KYC_SCREENING, name: 'KYC Screening', color: '#007BFF' },
  { id: CreditStatus.POST_SCREENING, name: 'Post Screening', color: '#00BFFF' },
  { id: CreditStatus.COMPLETED_FILE, name: 'Completed File', color: '#20C997' },
  { id: CreditStatus.BANK_REVISION, name: 'Analysis', color: '#20B2AA' },
  { id: CreditStatus.APPROVED, name: 'Approved', color: '#3CB371' },
  { id: CreditStatus.IN_NEGOTIATIONS, name: 'In Negotiations', color: '#2E8B57' },
  { id: CreditStatus.CONTRACT_IN_PROCESS, name: 'Contract In Process', color: '#008000' },
  { id: CreditStatus.SIGNED, name: 'Signed', color: '#006400' },
  { id: CreditStatus.REPAID, name: 'Repaid', color: '#6A5ACD' },
  { id: CreditStatus.DELAY, name: 'Delay', color: '#FFC82D' },
  { id: CreditStatus.RESTRUCTURE, name: 'Restructure', color: '#FF8C00' },
  { id: CreditStatus.DENIED, name: 'Denied', color: '#DC143C' },
];

export const creditStatusBeforeRejectionOptions: ColoredOption[] =
  creditStatusOptions.filter(s => s.id !== CreditStatus.DRAFT && s.id !== CreditStatus.DENIED);

export const creditStatusBlankOptions: ColoredOption[] = [
  ...creditStatusOptions,
  { id: 'No Credit' as CreditStatus, name: 'No Credit', color: '#A3A68A' },
];

export const csCreditStatuses = [
  CreditStatus.APPROVED,
  CreditStatus.IN_NEGOTIATIONS,
  CreditStatus.CONTRACT_IN_PROCESS,
  CreditStatus.SIGNED,
  CreditStatus.REPAID,
  CreditStatus.DELAY,
  CreditStatus.RESTRUCTURE,
];
export const cxStatusOptions = creditStatusOptions.filter(s => csCreditStatuses.includes(s.id));

export const getCreditStatusOptions = () => {
  const disabledStatuses: CreditStatus[] = [];

  if (rbac.isSuperadmin()) {
    // no extra restrictions for Superadmin or Role<Credit Status Control>
  } else if (rbac.hasCreditStatusControl()) {
    // no extra restrictions for Credit Status Control
  } else {
    disabledStatuses.push(CreditStatus.DRAFT);
    disabledStatuses.push(CreditStatus.BANK_REVISION);
    disabledStatuses.push(CreditStatus.APPROVED);
    disabledStatuses.push(CreditStatus.SIGNED);
    disabledStatuses.push(CreditStatus.REPAID);
    disabledStatuses.push(CreditStatus.RESTRUCTURE);
    disabledStatuses.push(CreditStatus.DELAY);
  }

  return creditStatusOptions.map(o => ({
    ...o,
    disabled: disabledStatuses.includes(o.id),
  }));
};

interface Props {
  source: string;
  creditStatus?: string;
  className?: string;
}

export const CreditStatusField = (props: Props & Partial<FunctionFieldProps>) => {
  const { creditStatus, className, ...restProps } = props;

  return (
    <FunctionField {...restProps} render={(record: any) => {
      const status = creditStatus || record.credit?.status || record[props.source];
      const statusName = creditStatusOptions.find(o => o.id === status || o.name === status)?.name;
      const color: any = creditStatusOptions.find(o => o.id === status || o.name === status)?.color;

      if (!status || !color) {
        return null;
      }

      return (
        <Chip
          label={statusName || status}
          size="small"
          style={{
            backgroundColor: typeof color === 'string'
              ? color
              : (color && color[500] ? color[500] : '#74706B'),
            color: 'white',
            height: 20,
            fontSize: 11,
            width: '100%',
          }}
          className={className}
        />
      );
    }} />
  );
};
